import "./style.scss";

import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Avatar, createFilterOptions, Skeleton } from "@mui/material";
import Plus from "assets/img/svg/plus";
import ModalBox from "components/Modal";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import StyledButton from "components/shared/StyledButton";
import { H3, SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import { getInitials } from "helpers/utils";
import { useNormalizedCompanyUsers } from "hooks/useNormalizedCompanyUsers";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { putValidatorsToAgreement } from "store/agreementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { IUser } from "types";

import variables from "../../assets/styles/_variables.module.scss";

interface AddValidatorsProps {
    templateId: number;
    validators: IUser[];
}

const AddValidators = (props: AddValidatorsProps) => {
    const { templateId, validators } = props;
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { data: users } = useAppSelector((state) => state.adminPanel.users.company);
    const [showAddValidatorModal, setShowAddValidatorModal] = useState(false);
    const [chosenValidators, setChosenValidators] = useState<IUser[]>([]);
    const [validatorsLocal, setValidatorsLocal] = useState<IUser[]>([]);
    const [isLoading, setIsLoading] = useState(!validators);
    const userList = useNormalizedCompanyUsers();

    useEffect(() => {
        if (validators) {
            setValidatorsLocal(validators);
            setChosenValidators(validators);
            setIsLoading(false);
        }
    }, [validators]);

    const handleAddValidator = () => {
        setShowAddValidatorModal(false);
        dispatch(
            putValidatorsToAgreement({
                id: templateId,
                validators: chosenValidators.map((validator: any, i) => ({ userId: validator.id, order: i })),
                type: "add-validator",
            }),
        );
        setValidatorsLocal(chosenValidators);
    };

    const handleOpenModal = () => {
        setShowAddValidatorModal(true);
    };

    const handleValidatorOrder = (from: number, to: number) => {
        if (to < 0 || to >= validatorsLocal.length) return;
        const newValidators = [...validatorsLocal];
        const element = newValidators[from];
        newValidators.splice(from, 1);
        newValidators.splice(to, 0, element);
        dispatch(
            putValidatorsToAgreement({
                id: templateId,
                validators: newValidators.map((validator: any, i) => ({ userId: validator.userId, order: i })),
                type: "change-order",
            }),
        );
        setValidatorsLocal(newValidators);
        setChosenValidators(newValidators);
    };

    const handleRemoveValidator = (index: number) => {
        const newValidators = [...validatorsLocal];
        newValidators.splice(index, 1);
        dispatch(
            putValidatorsToAgreement({
                id: templateId,
                validators: newValidators.map((validator: any, i) => ({ userId: validator.userId, order: i })),
                type: "remove-validator",
            }),
        );
        setValidatorsLocal(newValidators);
        setChosenValidators(newValidators);
    };

    const renderValidatorsOption = (props: any, option: any) => (
        <li {...props} className="social-profile-list" key={`validator-${option.id}`}>
            <div>
                <span style={{ fontSize: "14px", paddingLeft: option.depth * 12 }}>
                    {option?.firstname ?? ""} {option?.lastname ?? ""}
                </span>
                <SmallBody color="grey" style={{ fontWeight: 300 }}>
                    {option.email}
                </SmallBody>
            </div>
        </li>
    );

    const filterOptions = createFilterOptions({
        matchFrom: "any",
        stringify: (option: any) => `${option.firstname} ${option.lastname} ${option.email}`.toLowerCase(),
    });

    return (
        <div className="add-validators">
            <div className="add-validators-label">
                <SmallBodyHighlight color="grey">{t("ContractSettings.ContractValidators")}</SmallBodyHighlight>
                <SmallBody color="grey">{t("ContractSettings.ContractValidatorsDescription")}</SmallBody>
            </div>
            {isLoading ? (
                <>
                    <Skeleton width={"60%"} />
                    <Skeleton width={"50%"} />
                    <Skeleton width={"50%"} />
                </>
            ) : (
                <>
                    <div className="add-validators-list">
                        {validatorsLocal?.length == 0 ? (
                            <></>
                        ) : (
                            validatorsLocal?.map((validator, i) => {
                                const user = users.find((user) => +user.id === validator.userId);

                                return (
                                    <div key={validator.userId} className="add-validators-item">
                                        <div className="add-validators-item-name">
                                            <Avatar
                                                src={undefined}
                                                sx={{
                                                    backgroundColor: "#EEF3FD",
                                                    color: variables["foreground"],
                                                    fontSize: "10px",
                                                    height: "26px",
                                                    width: "26px",
                                                }}
                                            >
                                                {getInitials(`${user?.firstname ?? ""} ${user?.lastname ?? ""}`)}
                                            </Avatar>
                                            <SmallBody>
                                                {user?.firstname ?? ""} {user?.lastname ?? ""}
                                            </SmallBody>
                                        </div>
                                        <div className="add-validators-item-actions">
                                            <div className="add-validators-item-arrows">
                                                <KeyboardArrowDownIcon onClick={() => handleValidatorOrder(i, i + 1)} />
                                                <KeyboardArrowUpIcon onClick={() => handleValidatorOrder(i, i - 1)} />
                                            </div>
                                            <div className="add-validators-item-delete">
                                                <CloseIcon
                                                    onClick={() => handleRemoveValidator(i)}
                                                    sx={{
                                                        height: "18px",
                                                        width: "18px",
                                                        "&:hover": { color: "var(--danger) !important" },
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    <StyledButton buttonType="outlined" onClick={handleOpenModal}>
                        <Plus />
                        {t("ContractSettings.AddValidator")}
                    </StyledButton>
                </>
            )}

            <ModalBox
                open={showAddValidatorModal}
                onClose={() => setShowAddValidatorModal(false)}
                mainTitle={t("ContractSettings.AddValidator")}
            >
                <div style={{ display: "flex", flexFlow: "column", gap: "16px" }}>
                    <AutocompleteFormComponent
                        label={
                            <H3 color="grey" style={{ paddingLeft: 0 }}>
                                {t("ContractSettings.Validators")}
                            </H3>
                        }
                        options={userList}
                        value={chosenValidators}
                        onChange={(e: any, value: any) => setChosenValidators(value)}
                        renderOption={renderValidatorsOption}
                        renderOptionText={(option) => `${option?.firstname ?? ""} ${option?.lastname ?? ""}`}
                        multiple
                        limitTags={2}
                        filterOptions={filterOptions}
                    />
                    <StyledButton buttonType="primary" fullWidth onClick={handleAddValidator}>
                        <Plus />
                        {t("ContractSettings.Add")}
                    </StyledButton>
                </div>
            </ModalBox>
        </div>
    );
};

export default AddValidators;
